import { LegalEntitySystemTableModel, LegalEntitySystemViewModel } from "./LegalEntitySystemTab.types";
import { ILegalEntityAssetsTab } from "./LegalEntitySystemTab";
import { useTranslation } from "@localization/useTranslation";
import { useDotLegalQuery } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { getUniqueFilterOptions, getUniqueNestedFilterOptions, sortByEnumValue, sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";

export default function useLegalEntitySystemTabHooks(props: ILegalEntityAssetsTab) {
    const { translateString, translateLegalEntityAssetRole } = useTranslation();
    const url = `/legalEntitySystems/${props.legalEntityId}`;
    let { isLoading, data } = useDotLegalQuery<Array<LegalEntitySystemViewModel>>(url);

    const { selectFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `legalEntity-${props.legalEntityId}-assets-filters`
    );

    const allRows: Array<LegalEntitySystemTableModel> = (data ?? []).map((x) => {
        return {
            id: x.id,
            name: x.name,
            roleIds: x.roles.map((role) => role.toString()),
            roles: x.roles.map((role) => translateLegalEntityAssetRole(role.toString())).join(", "),
        };
    });

    const systems = applyClientFilters(
        allRows,
        [
            { id: "name", candidate: (row) => row.name },
            { id: "role", candidate: (row) => row.roleIds },
        ],
        selectFilters,
        {},
        filterOperators
    );

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "name",
                    label: translateString("asset"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.name ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (row) => row.name).sort(sortByName),
                },
                {
                    id: "role",
                    label: translateString("role"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.role ?? [],
                    fixedData: getUniqueNestedFilterOptions(
                        data,
                        (row) => row.roles,
                        (role) => translateLegalEntityAssetRole(role.toString()),
                        (role) => role.toString()
                    ).sort(sortByEnumValue),
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0);

    return {
        isLoading,
        systems: systems ?? [],
        hasData: isLoading || allRows.length > 0,
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
    };
}
